import {
  MapPin,
  UserPlus,
  Play,
  Square,
  CheckCircle,
  CreditCard,
  CheckCheck,
  Shield,
  FileText,
  Package,
  MessageSquare,
  ReceiptText,
} from 'lucide-react';
import i18n from '../../../../i18n';

const lng = localStorage.getItem('I18N_LANGUAGE');

const ProgressTracker = ({ auctions }) => {
  return (
    <div>
      <style>{`
        .progress-container {
          background-color: #f8f9fa;
          min-height: 100vh;
          padding: 3rem 0;
        }

        .item-head {
          margin-top: 0.75rem;
          text-align: center;
          font-size: 0.75rem;
          font-weight: 500;
          line-height: 1.2;
          color: #212529;
        }

        .item-date {
          font-size: 0.7rem;
          color: #6c757d;
          margin-top: 0.7rem;
        }

        .track-item {
          background: white;
          border-radius: 8px;
          padding: 1.5rem;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
      `}</style>

      <div className="progress-container rounded-2">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-12">
              {/* Progress Tracker */}
              {auctions.length > 0 &&
                auctions.map((item, i) => {
                  return <ListItem key={item.auction_id || i} item={item} index={i} />;
                })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressTracker;

function ListItem({ item, index }) {
  const newSteps = [
    { key: 'site_visit', label: i18n.t('site_visit'), icon: MapPin },
    { key: 'registration', label: i18n.t('registration'), icon: UserPlus },
    { key: 'auction_start', label: i18n.t('auction_start'), icon: Play },
    { key: 'auction_end', label: i18n.t('auction_end'), icon: Square },
    { key: 'rop_approval', label: i18n.t('rop_approval'), icon: CheckCircle },
    { key: 'invoice_send', label: i18n.t('invoice_send'), icon: ReceiptText },
    { key: 'paid', label: i18n.t('paid'), icon: CheckCheck },
    { key: 'paid_guarantee', label: i18n.t('paid_guarantee'), icon: Shield },
    { key: 'approval_paper', label: i18n.t('approval_paper'), icon: FileText },
    { key: 'items_received', label: i18n.t('items_received'), icon: Package },
    { key: 'rop_paid', label: i18n.t('rop_paid'), icon: CreditCard },
    { key: 'feedback', label: i18n.t('feedback'), icon: MessageSquare },
  ];
  const styles = {
    container: {
      width: '100%',
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '24px',
      overflowX: 'auto',
      direction: lng === 'ar' ? 'rtl' : 'ltr',
    },
    progressContainer: {
      position: 'relative',
    },
    progressLine: {
      position: 'absolute',
      top: '24px',
      left: '0',
      right: '0',
      height: '2px',
      backgroundColor: '#e5e7eb',
      zIndex: 1,
    },
    progressFill: {
      height: '100%',
      // backgroundColor: '#284780',
      transition: 'width 0.5s ease-in-out',
      transformOrigin: lng === 'ar' ? 'right' : 'left',
    },
    stepsContainer: {
      position: 'relative',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      zIndex: 2,
    },
    stepWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minWidth: '120px',
      flex: '1',
      maxWidth: `${100 / newSteps.length}%`,
    },
    stepCircle: {
      position: 'relative',
      zIndex: 10,
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      border: '2px solid',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backgroundColor: 'white',
    },
    stepCircleCompleted: {
      backgroundColor: '#284780',
      borderColor: '#284780',
      color: 'white',
    },
    stepCircleActive: {
      backgroundColor: 'white',
      borderColor: '#284780',
      color: '#284780',
      boxShadow: '0 4px 12px rgba(79, 70, 229, 0.15), 0 0 0 4px rgba(79, 70, 229, 0.1)',
      transform: 'scale(1.05)',
    },
    stepCircleUpcoming: {
      backgroundColor: 'white',
      borderColor: '#d1d5db',
      color: '#9ca3af',
    },
    stepLabel: {
      marginTop: '12px',
      textAlign: 'center',
      padding: '0 4px',
    },
    stepLabelText: {
      fontSize: '14px',
      fontWeight: '500',
      lineHeight: '1.3',
      margin: '0',
    },
    stepLabelCompleted: {
      color: '#374151',
    },
    stepLabelActive: {
      color: '#284780',
    },
    stepLabelUpcoming: {
      color: '#6b7280',
    },
  };
  return (
    <div className="track-item my-3">
      <h5 className="auction-title">
        {index + 1}. {lng === 'ar' ? item.auction_title_ar : item.auction_title} (ID: {item.auction_id})
      </h5>

      <div className="row">
        <div className="col">
          <div style={styles.container}>
            {/* Progress Tracker */}
            <div style={styles.progressContainer}>
              {/* Progress Line */}
              <div style={{ ...styles.progressLine, width: `${newSteps.length * 120}px` }}>
                <div style={styles.progressFill} />
              </div>
              {/* Steps Container */}
              <div style={styles.stepsContainer}>
                {newSteps.map((step) => {
                  const stepData = item[step.key] || { status: 'N', date: '' };
                  const isCompleted = stepData.status === 'A';
                  const IconComponent = step.icon;

                  let circleStyle = { ...styles.stepCircle };
                  let labelStyle = { ...styles.stepLabelText };

                  if (isCompleted) {
                    circleStyle = { ...circleStyle, ...styles.stepCircleCompleted };
                    labelStyle = { ...labelStyle, ...styles.stepLabelCompleted };
                  } else {
                    circleStyle = { ...circleStyle, ...styles.stepCircleUpcoming };
                    labelStyle = { ...labelStyle, ...styles.stepLabelUpcoming };
                  }

                  return (
                    <div key={step.key} style={styles.stepWrapper}>
                      {/* Step Circle */}
                      <div
                        style={circleStyle}
                        onMouseEnter={(e) => {
                          if (isCompleted) {
                            e.target.style.transform = 'scale(1.05)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isCompleted) {
                            e.target.style.transform = 'scale(1)';
                          }
                        }}
                      >
                        <IconComponent size={18} />
                      </div>

                      {/* Step Label */}
                      <div style={styles.stepLabel}>
                        <p style={labelStyle}>{step.label}</p>
                        {/* <p className="item-head">{step.label}</p> */}
                        <p className="item-date">{stepData.date || ''}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
